import { UnitEnum } from './types/units';

type ProjectOverviewItem = {
  body: string;
  images: { name: string; caption: string }[];
};

export type SpeciesGroupId = `g${number}`;

export enum ConservationMethodology {
  vnu = 'vnu', // Verifiable Nature Unit
  bu = 'bu', // Biodiversity Unit
}

export type Project = {
  id: string;
  natureUnitVariant: ConservationMethodology;
  name: string;
  location_description: string;
  biomes: string[];
  organization: {
    id: string;
    name: string;
  };
  area: number;
  areaUnits: UnitEnum;
  ecoregions: string[];
  speciesGroups: Record<SpeciesGroupId, string>;
  overview: {
    about: ProjectOverviewItem;
    species: ProjectOverviewItem;
    community: ProjectOverviewItem;
  };
  subtype: 'restoration' | 'conservation';
};

export type CellProperties = {
  id?: string;
  name: string;
  area: number;
  area_cut: number;
  cut: boolean;
  parent_id: string;
  threats_ts: string;
  threats: number;
  threats_i: number;
  threats_i_ts: Record<string, number>;
  built_ts: string;
  built: number;
  crop_ts: string;
  crop: number;
  edge_ts: string;
  edge: number;
  mine_ts: string;
  mine: number;
  road_ts: string;
  road: number;
  treeloss_ts: string;
  treeloss: number;
  index: number;
  freshwater_habitat: number;
  [speciesGroupId: SpeciesGroupId]: number;
  indicator_species_score: number;
  ecological_integrity: number;
  threats_i_change: number;
  threats_i_change_percentage: number;
  // TODO: MVP-5183 decide contract with science on fact names
  ecological_integrity_ts: number;
};

export type Tileset = {
  id: string;
  sourceLayer: string;
};

export type Observation = {
  species: string;
  key: number;
  year: number;
  decimalLatitude: number;
  decimalLongitude: number;
  geometry: string;
  references?: string;
  image_url?: string;
  source: string;
  eventDate?: string;
  indicator_group: SpeciesGroupId;
};

export enum Threat {
  built = 'built',
  treeloss = 'treeloss',
  crop = 'crop',
  road = 'road',
  mine = 'mine',
  overgrazing = 'overgrazing',
  other = 'other',
}

export type FactType =
  | 'r1_conservation_current_year'
  | 'r1_conservation_baseline_year'
  | 'r1_conservation_no_nature_units'
  | 'r1_conservation_ecological_integrity'
  | 'r1_conservation_ecological_integrity_baseline'
  | 'r1_conservation_ecological_integrity_lowest'
  | 'r1_conservation_ecological_integrity_highest'
  | 'r1_conservation_ecological_integrity_ts'
  | 'r1_conservation_ecological_integrity_change_percentage'
  | 'r1_conservation_habitat_intactness_change_percentage'
  | 'r1_conservation_threats_change_percentage'
  | 'r1_conservation_indicator_species_change_percentage'
  | 'r1_conservation_species_group_presence'
  | 'r1_conservation_species_group_presence_ts'
  | 'r1_conservation_degradation_drivers_i_baseline'
  | 'r1_conservation_degradation_drivers_i'
  | 'r1_conservation_threats_i'
  | 'r1_conservation_threats_i_baseline'
  | 'r1_conservation_threats_baseline_year'
  | 'r1_conservation_habitat_intactness_baseline_year'
  | 'r1_conservation_indicator_species_baseline_year'
  | 'r1_conservation_ecological_integrity_baseline_year'
  | 'r1_conservation_threats_i_ts'
  | 'r1_conservation_threats_ts'
  | 'r1_conservation_threats_i_1_year_change'
  | 'r1_conservation_indicator_species_score'
  | 'r1_conservation_indicator_species_baseline_year_score'
  | 'r1_conservation_indicator_species_score_1_year_change'
  | 'r1_conservation_indicator_species_score_baseline_year'
  | 'r1_conservation_indicator_species_ts'
  | 'r1_conservation_groups_detected'
  | 'r1_conservation_groups_total'
  | 'r1_conservation_drivers_total'
  | 'r1_conservation_drivers_detected'
  | 'r1_conservation_species_pct_historic_graph'
  | 'r1_conservation_habitat_intactness_historic_index_graph'
  | 'r1_conservation_habitat_intactness_historic_graph'
  | 'r1_conservation_detected_threats_breakdown'
  | 'r1_conservation_built'
  | 'r1_conservation_built_ts'
  | 'r1_conservation_crop'
  | 'r1_conservation_crop_ts'
  | 'r1_conservation_edge'
  | 'r1_conservation_edge_ts'
  | 'r1_conservation_mine'
  | 'r1_conservation_mine_ts'
  | 'r1_conservation_road'
  | 'r1_conservation_road_ts'
  | 'r1_conservation_treeloss'
  | 'r1_conservation_treeloss_ts'
  | 'r1_conservation_habitat_intactness_historic_explainer'
  | 'r1_conservation_degradation_drivers_historic_explainer'
  | 'r1_conservation_indicator_species_historic_explainer'
  | 'r1_conservation_ecological_integrity_historic_explainer'
  | 'r1_conservation_ecological_water_historic_explainer'
  | 'r1_water_holding_capacity_per_ha'
  | 'r1_water_holding_capacity_latest_analysis_per_ha'
  | 'r1_water_holding_capacity_latest_analysis_total'
  | 'r1_historic_water_and_soil_moisture_graph'
  | 'r1_historic_water_and_soil_moisture_per_ha_graph'
  | 'r1_soil_moisture_latest_analysis_total'
  | 'r1_soil_moisture_latest_analysis_per_ha'
  | 'r1_above_ground_carbon_i'
  | 'r1_above_ground_carbon_baseline'
  | 'r1_above_ground_carbon_pct_change'
  | 'r1_conservation_ndvi_baseline_year'
  | 'r1_conservation_ndvi_i'
  | 'r1_conservation_ndvi_baseline'
  | 'r1_conservation_ndvi_change_percentage'
  | 'r1_conservation_ndvi_ts'
  | 'r1_conservation_ndvi_historic_explainer';

export type Fact<N extends FactType = FactType, V = unknown> = {
  name: N;
  value: V;
  unit: UnitEnum;
  measured_at?: string | null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FactLike<N = any, V = any> = {
  name: N;
  value: V;
  unit: UnitEnum;
  measured_at?: string | null;
};

export type Dashboard = {
  facts: Fact[];
};

export enum TrendLabel {
  increasing = 'increasing',
  stable = 'stable',
  decreasing = 'decreasing',
}

export enum ConservationBenchmarkType {
  thriving = 'thriving',
  healthy = 'healthy',
  managing = 'managing',
  fragile = 'fragile',
  degraded = 'degraded',
}
